import { get, each } from 'lodash-es'
import * as Sentry from '@sentry/react'
import { isDevelopmentEnvironment, isProductionEnvironment } from '@utils/environment.ts'

// Polyfill for Object.hasOwn
if (!Object.hasOwn) {
  Object.hasOwn = function (obj, prop) {
    return Object.prototype.hasOwnProperty.call(obj, prop)
  }
}

// May not be available, e.g. during headless tests
if (typeof window !== 'undefined') {
  // Ensure console functions exist, (mainly for IE when console isn't open)
  if (window.console === undefined) {
    window.console = {}
  }
  each(['log', 'warn', 'info', 'dir'], functionName => {
    if (!window.console.hasOwnProperty(functionName)) {
      window.console[functionName] = function (obj) {}
    }
  })

  if (typeof window.Timetoast === 'undefined') {
    window.Timetoast = {}
  }

  window.Timetoast.throwTestError = function () {
    try {
      throw new Error('Test error')
    } catch (error) {
      console.error('Test error thrown:', error)
      throw error
    }
  }
}

const initSentry = isProductionEnvironment // || isDevelopmentEnvironment
if (initSentry) {
  // just skip unhandled rejection errors in production. Even with the beforeSend hook below, we are still eating up quotas
  const ON_UNHANDLED_REJECTION = !isProductionEnvironment

  // Note: Errors thrown manually in the console aren't caught by Sentry
  Sentry.init({
    dsn: 'https://c8af6e959e754fd98ca7f66d5a56e952@o143308.ingest.us.sentry.io/1131496',
    environment: process.env.NODE_ENV,

    allowUrls: [
      // cloudfront cdn
      /https?:\/\/((cdn|www)\.)?timetoast\.com/,
      /localhost/,
    ],
    denyUrls: [
      // Google Adsense
      /pagead\/js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],
    integrations: [
      Sentry.extraErrorDataIntegration(),
      Sentry.globalHandlersIntegration({
        onunhandledrejection: ON_UNHANDLED_REJECTION, // Note: skips when set to false
      }),
      // Note Don't use thirdPartyErrorFilterIntegration as it filters events from CDN bundles.
      // See: https://docs.sentry.io/platforms/javascript/configuration/filtering/
    ],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.', // See: https://forum.sentry.io/t/resizeobserver-loop-limit-exceeded/8402/4
      'Cannot read properties of null (reading "defaultView")',
    ],
    beforeSend(event, hint) {
      if (isDevelopmentEnvironment) {
        //console.log('Sentry event:', event)
        //console.error(hint.originalException || hint.syntheticException);
        return null // this drops the event and nothing will be sent to sentry
      } else {
        // this is here because unhandled promise rejection errors cause by Chrome extensions were eating up Sentry quotas.
        // See: https://github.com/getsentry/sentry-javascript/issues/2210
        try {
          const message = get(event, ['extra', '__serialized__', 'detail', 'reason', 'message'])
          if (message === 'Extension context invalidated.') {
            return null
          }
        } catch (_o_O) {
          return event
        }
        return event
      }
    },
  })
}
